import { z } from 'zod';

/**
 * Login único (OpenID Connect) por empresa cliente (T-028c, RF-04; DEC-072): a equipe Fluxus configura o provedor
 * (emissor, client id e secret), os domínios de e-mail atendidos e quem pode entrar.
 */

export const SSO_USER_POLICIES = ['CREATE', 'EXISTING_ONLY'] as const;
export type SsoUserPolicy = (typeof SSO_USER_POLICIES)[number];
export const SSO_USER_POLICY_LABELS: Record<SsoUserPolicy, string> = {
  CREATE: 'Criar a conta no primeiro acesso, com o perfil padrão',
  EXISTING_ONLY: 'Só quem já tem conta na empresa (ativa ou com convite pendente)',
};

export const SSO_MAX_DOMAINS = 20;

/** Domínio de e-mail: minúsculas, rótulos separados por ponto (ex.: cliente.com.br). */
export const ssoDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((v) => v.replace(/^@/, ''))
  .pipe(
    z
      .string()
      .min(3, 'Informe o domínio.')
      .max(253, 'Domínio longo demais.')
      .regex(
        /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/,
        'Domínio inválido (ex.: empresa.com.br).',
      ),
  );

const parseUrl = (v: string): URL | null => {
  try {
    return new URL(v);
  } catch {
    return null;
  }
};

/** Emissor (issuer) do provedor: endereço https; http só para o próprio computador (desenvolvimento). */
export const ssoIssuerSchema = z
  .string()
  .trim()
  .min(1, 'Informe o emissor.')
  .max(512, 'Endereço longo demais.')
  .superRefine((v, ctx) => {
    const url = parseUrl(v);
    const message = !url
      ? 'Endereço inválido (ex.: https://login.empresa.com.br/realms/empresa).'
      : url.protocol !== 'https:' &&
          !(url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname))
        ? 'Use um endereço https (http só para localhost).'
        : url.search || url.hash || url.username || url.password
          ? 'O emissor não leva usuário, parâmetros (?) nem âncora (#).'
          : null;
    if (message) ctx.addIssue({ code: 'custom', message });
  })
  .transform((v) => v.replace(/\/+$/, ''));

export const ssoConfigInputSchema = z
  .object({
    enabled: z.boolean(),
    issuer: ssoIssuerSchema,
    clientId: z.string().trim().min(1, 'Informe o client id.').max(255, 'Client id longo demais.'),
    /** Omitido (ou vazio) = mantém o secret já guardado. Obrigatório na primeira configuração. */
    clientSecret: z
      .string()
      .max(512, 'Client secret longo demais.')
      .optional()
      .transform((v) => (v && v.trim() ? v.trim() : undefined)),
    domains: z
      .array(ssoDomainSchema)
      .min(1, 'Informe pelo menos um domínio de e-mail.')
      .max(SSO_MAX_DOMAINS, `No máximo ${SSO_MAX_DOMAINS} domínios.`)
      .refine((list) => new Set(list).size === list.length, 'Há domínios repetidos.'),
    userPolicy: z.enum(SSO_USER_POLICIES),
    defaultRoleId: z.uuid().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.userPolicy === 'CREATE' && !v.defaultRoleId) {
      ctx.addIssue({
        code: 'custom',
        path: ['defaultRoleId'],
        message: 'Escolha o perfil de quem for criado pelo SSO.',
      });
    }
  });
export type SsoConfigInput = z.input<typeof ssoConfigInputSchema>;

export const ssoConfigSchema = z.object({
  enabled: z.boolean(),
  issuer: z.string(),
  clientId: z.string(),
  /** O secret nunca sai da API: só se está guardado. */
  clientSecretSet: z.boolean(),
  domains: z.array(z.string()),
  userPolicy: z.enum(SSO_USER_POLICIES),
  defaultRole: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  updatedAt: z.string(),
});
export type SsoConfig = z.infer<typeof ssoConfigSchema>;

/**
 * Configuração da empresa (null = ainda não configurado) e o endereço de retorno (redirect URI) que a empresa cadastra
 * no provedor para o Fluxus BSC.
 */
export const ssoConfigResponseSchema = z.object({
  config: ssoConfigSchema.nullable(),
  callbackUrl: z.string(),
});
export type SsoConfigResponse = z.infer<typeof ssoConfigResponseSchema>;

export const ssoTestInputSchema = z.object({ issuer: ssoIssuerSchema });
export type SsoTestInput = z.input<typeof ssoTestInputSchema>;

/** Resultado do teste de conexão (descoberta do emissor e chaves de assinatura). */
export const ssoTestResultSchema = z.object({
  issuer: z.string(),
  authorizationEndpoint: z.string(),
  tokenEndpoint: z.string(),
  /** Chaves públicas de assinatura publicadas pelo provedor. */
  signingKeys: z.number().int(),
});
export type SsoTestResult = z.infer<typeof ssoTestResultSchema>;
